
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMinInstallationDate } from '@/utils/dateUtils';
import { determineIfMultipleUnitsNeeded, getAvailableTimeSlots } from '@/utils/installationUtils';
import DatePickerSection from './installation/DatePickerSection';
import TimeSlotSection from './installation/TimeSlotSection';
import InstallationInfoSection from './installation/InstallationInfoSection';

interface InstallationDatePickerProps {
  selectedOffer: 'standard' | 'premium' | 'exclusive';
  customerName: string;
  onSchedulingComplete?: (date: string, timeSlot: string) => void;
  numberOfRooms: number;
  squareArea: number;
}

const InstallationDatePicker: React.FC<InstallationDatePickerProps> = ({ 
  selectedOffer,
  customerName,
  onSchedulingComplete,
  numberOfRooms,
  squareArea
}) => {
  const navigate = useNavigate();
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [timeSlot, setTimeSlot] = useState<string | undefined>(undefined);
  const [defaultMonth, setDefaultMonth] = useState<Date>(new Date());
  
  // Determine if multiple units are needed based on room count and area
  const needsMultipleUnits = determineIfMultipleUnitsNeeded(numberOfRooms, squareArea);
  
  // Log values to debug the issue
  console.log("InstallationDatePicker - input values:", { numberOfRooms, squareArea });
  console.log("InstallationDatePicker - needsMultipleUnits:", needsMultipleUnits);
  
  // Get available time slots based on installation requirements
  const timeSlots = getAvailableTimeSlots(needsMultipleUnits);

  // Set the default month to the month containing the earliest available date
  useEffect(() => {
    // This ensures we use the exact date of minDate, not just the month
    setDefaultMonth(getMinInstallationDate());
    console.log("InstallationDatePicker useEffect - values:", { numberOfRooms, squareArea });
  }, []);
  
  // Reset timeSlot when date changes or when needsMultipleUnits changes
  useEffect(() => {
    setTimeSlot(undefined);
  }, [date, needsMultipleUnits]);
  
  const handleSubmit = () => {
    if (!date || !timeSlot) {
      toast.error("Wybierz termin montażu", {
        description: "Proszę wybrać datę i godzinę montażu"
      });
      return;
    }
    
    // Format the date for display
    const formattedDate = format(date, 'EEEE, d MMMM yyyy', { locale: pl });
    
    toast.success("Montaż zaplanowany!", {
      description: `Twój montaż został zaplanowany na ${formattedDate}, ${timeSlot}`
    });
    
    console.log("handleSubmit - scheduling complete with values:", { 
      formattedDate, 
      timeSlot, 
      numberOfRooms, 
      squareArea 
    });
    
    if (onSchedulingComplete) {
      onSchedulingComplete(formattedDate, timeSlot);
    } else {
      // Store scheduling info in sessionStorage
      sessionStorage.setItem('schedulingInfo', JSON.stringify({ 
        date: formattedDate, 
        time: timeSlot,
        numberOfRooms,
        squareArea
      }));
      
      // Redirect to success page
      setTimeout(() => {
        navigate('/payment-success');
      }, 1500);
    }
  };
  
  return (
    <div className="w-full max-w-4xl mx-auto opacity-0 animate-fadeIn">
      <div className="glass-card p-6 md:p-8">
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <div className="bg-primary/10 p-2 rounded-full">
              <CalendarIcon className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl md:text-2xl font-semibold">Wybierz termin montażu</h2>
          </div>
          
          <InstallationInfoSection 
            customerName={customerName}
            selectedOffer={selectedOffer}
            needsMultipleUnits={needsMultipleUnits}
            numberOfRooms={numberOfRooms}
            squareArea={squareArea}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <DatePickerSection 
              date={date}
              setDate={setDate}
              defaultMonth={defaultMonth}
            />
            
            <TimeSlotSection 
              timeSlots={timeSlots}
              selectedTimeSlot={timeSlot}
              setTimeSlot={setTimeSlot}
              dateSelected={!!date}
            />
          </div>
          
          <Button 
            type="button" 
            className="w-full py-6 bg-primary hover:bg-primary-dark text-white rounded-xl transition-colors duration-200 font-medium"
            onClick={handleSubmit}
            disabled={!date || !timeSlot}
          >
            Potwierdź termin montażu
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstallationDatePicker;
